import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  BookOpen, CheckCircle2, Clock, Play, Heart, Star, Flag,
  Edit3, Share2, Loader2, ChevronLeft, ChevronRight, Search,
  TrendingUp, BarChart3, Target, Filter, X, Copy, MessageSquare
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "../../components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { AdminLayout } from "../../components/layout/AdminLayout";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import ShareDialog from "../../components/shared/ShareDialog";
import { toast } from "sonner";

const typeLabels = { "scorm_1.2": "eLearning", "scorm_2004": "eLearning", "ilt": "ILT", "video": "Video", "document": "Document" };
const statusColors = {
  completed: "bg-green-500/10 text-green-600 border-green-500/20",
  in_progress: "bg-blue-500/10 text-blue-600 border-blue-500/20",
  enrolled: "bg-muted text-muted-foreground",
};

const MyLearningPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [recommended, setRecommended] = useState([]);
  const [sharedWithMe, setSharedWithMe] = useState([]);
  const [activeTab, setActiveTab] = useState("my-courses");

  // Dialogs
  const [feedbackDialog, setFeedbackDialog] = useState(null);
  const [reportDialog, setReportDialog] = useState(null);
  const [suggestDialog, setSuggestDialog] = useState(null);
  const [shareDialog, setShareDialog] = useState(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");

  const PAGE_SIZE = 50;

  const fetchCourses = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (typeFilter === "elearning") params.append("course_type", "elearning");
      else if (typeFilter === "ilt") params.append("course_type", "ilt");
      params.append("page", page);
      params.append("page_size", PAGE_SIZE);
      const res = await api.get(`/my/learning?${params.toString()}`);
      setCourses(res.data?.courses || []);
      setStats(res.data?.stats || {});
      setTotal(res.data?.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [typeFilter, page]);

  const fetchRecommended = useCallback(async () => {
    try {
      const res = await api.get("/courses/recommended");
      setRecommended((res.data || []).slice(0, 6));
    } catch (e) { /* ignore */ }
  }, []);

  const fetchShared = useCallback(async () => {
    try {
      const res = await api.get("/my/shared-with-me");
      setSharedWithMe(res.data || []);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { fetchCourses(); }, [fetchCourses]);
  useEffect(() => { fetchRecommended(); fetchShared(); }, [fetchRecommended, fetchShared]);

  const toggleFavorite = async (courseId) => {
    try {
      const res = await api.post(`/courses/${courseId}/favorite`);
      setCourses(prev => prev.map(c => c.course_id === courseId ? { ...c, is_favorite: res.data.favorited } : c));
      toast.success(res.data.favorited ? "Added to favorites" : "Removed from favorites");
    } catch (e) { toast.error("Failed"); }
  };

  const submitFeedback = async () => {
    if (!feedbackDialog) return;
    try {
      await api.post(`/courses/${feedbackDialog.course_id}/feedback`, { rating, comment });
      toast.success("Feedback submitted to TTS");
      setFeedbackDialog(null); setRating(5); setComment("");
    } catch (e) { toast.error("Failed"); }
  };

  const submitReport = async () => {
    if (!reportDialog) return;
    try {
      await api.post(`/courses/${reportDialog.course_id}/report`, { report_type: "content_issue", description: comment });
      toast.success("Report submitted to TTS");
      setReportDialog(null); setComment("");
    } catch (e) { toast.error("Failed"); }
  };

  const submitSuggestion = async () => {
    if (!suggestDialog) return;
    try {
      await api.post(`/courses/${suggestDialog.course_id}/suggest`, { suggestion: comment });
      toast.success("Suggestion submitted to TTS");
      setSuggestDialog(null); setComment("");
    } catch (e) { toast.error("Failed"); }
  };

  const openShareDialog = (course) => {
    setShareDialog(course);
  };

  const copyShareLink = (courseId) => {
    navigator.clipboard.writeText(`${window.location.origin}/course/${courseId}`);
    toast.success("Course link copied");
  };

  const filtered = courses.filter(c => {
    if (statusFilter !== "all" && c.status !== statusFilter) return false;
    if (search && !c.title?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const pageContent = (
    <div className="space-y-6" data-testid="my-learning-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }} data-testid="my-learning-title">My Courses</h1>
        <p className="text-muted-foreground text-sm">View, manage, and continue your assigned courses</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: "Total Modules", value: stats.total_modules || 0, icon: BookOpen, color: "text-blue-500" },
          { label: "Completed", value: stats.completed || 0, icon: CheckCircle2, color: "text-green-500" },
          { label: "In Progress", value: stats.in_progress || 0, icon: Clock, color: "text-amber-500" },
          { label: "Not Started", value: stats.not_started || 0, icon: Target, color: "text-muted-foreground" },
          { label: "Completion Rate", value: `${stats.completion_rate || 0}%`, icon: TrendingUp, color: "text-primary" },
        ].map((st, i) => (
          <Card key={i}>
            <CardContent className="p-4 text-center">
              <st.icon className={`w-5 h-5 mx-auto mb-1 ${st.color}`} />
              <p className="text-xl font-bold">{st.value}</p>
              <p className="text-[10px] text-muted-foreground">{st.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Overall Progress */}
      <Card>
        <CardContent className="py-4 px-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Overall Progress</span>
            <span className="text-sm font-bold text-primary">{stats.completion_rate || 0}%</span>
          </div>
          <Progress value={stats.completion_rate || 0} className="h-2.5" />
          <p className="text-xs text-muted-foreground mt-1.5">{stats.completed || 0} of {stats.total_modules || 0} modules completed</p>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap gap-1">
          <TabsTrigger value="my-courses" data-testid="tab-my-courses">My Courses</TabsTrigger>
          <TabsTrigger value="favorites" data-testid="tab-favorites">Favorites</TabsTrigger>
          <TabsTrigger value="recommended" data-testid="tab-recommended">Recommended by TTS</TabsTrigger>
          <TabsTrigger value="shared" data-testid="tab-shared">Shared with Me {sharedWithMe.filter(s => !s.read).length > 0 && <Badge className="ml-1 bg-red-500 text-white text-[9px] px-1 py-0">{sharedWithMe.filter(s => !s.read).length}</Badge>}</TabsTrigger>
        </TabsList>

        {/* My Courses Tab */}
        <TabsContent value="my-courses" className="mt-4 space-y-4">
          {/* Filters */}
          <div className="flex items-center gap-3 flex-wrap" data-testid="course-filters">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search courses..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 h-9" data-testid="course-search" />
            </div>
            {/* Type Toggle */}
            <div className="flex border rounded-lg overflow-hidden" data-testid="type-toggle">
              {[
                { val: "all", label: "All" },
                { val: "elearning", label: "eLearning" },
                { val: "ilt", label: "ILT" },
              ].map(t => (
                <button key={t.val} onClick={() => { setTypeFilter(t.val); setPage(1); }}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${typeFilter === t.val ? "bg-primary text-primary-foreground" : "bg-muted/30 text-muted-foreground hover:bg-muted"}`}>
                  {t.label}
                </button>
              ))}
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[130px] h-9 text-xs" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="enrolled">Not Started</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Course List */}
          {loading ? (
            <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <BookOpen className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p>No courses found</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map(c => (
                <CourseRow key={c.enrollment_id} course={c} navigate={navigate}
                  onFavorite={toggleFavorite} onFeedback={setFeedbackDialog} onReport={setReportDialog}
                  onSuggest={setSuggestDialog} onShare={openShareDialog} onCopyLink={copyShareLink} />
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4" data-testid="pagination">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </TabsContent>

        {/* Favorites Tab */}
        <TabsContent value="favorites" className="mt-4">
          {filtered.filter(c => c.is_favorite).length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Heart className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p>No favorites yet</p>
              <p className="text-xs mt-1">Click the heart icon on any course to add it here</p>
            </div>
          ) : (
            <div className="space-y-2">
              {courses.filter(c => c.is_favorite).map(c => (
                <CourseRow key={c.enrollment_id} course={c} navigate={navigate}
                  onFavorite={toggleFavorite} onFeedback={setFeedbackDialog} onReport={setReportDialog}
                  onSuggest={setSuggestDialog} onShare={openShareDialog} onCopyLink={copyShareLink} />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Recommended Tab */}
        <TabsContent value="recommended" className="mt-4">
          {recommended.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <TrendingUp className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p>No recommendations yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {recommended.map(r => (
                <Card key={r.id} className="cursor-pointer hover:border-primary/30 transition-colors overflow-hidden" onClick={() => navigate(`/course/${r.id}`)}>
                  <div className="h-28 relative" style={r.thumbnail_url ? {
                    background: `linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.5)), url(${r.thumbnail_url}) center/cover`
                  } : { background: "linear-gradient(to br, #3b82f6, #1e40af)" }}>
                    <Badge className="absolute top-2 left-2 bg-blue-500/90 text-white text-[10px]">TTS Recommended</Badge>
                  </div>
                  <CardContent className="p-3">
                    <h4 className="font-medium text-sm truncate">{r.title}</h4>
                    <p className="text-[10px] text-muted-foreground mt-0.5">{r.category} · {r.duration_hours}h</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Shared with Me Tab */}
        <TabsContent value="shared" className="mt-4">
          {sharedWithMe.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Share2 className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p>No shared courses yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {sharedWithMe.map(s => (
                <div key={s.id} className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer hover:bg-muted/30 ${!s.read ? "bg-blue-500/5 border-blue-500/20" : ""}`}
                  onClick={async () => {
                    if (!s.read) await api.put(`/my/shared/${s.id}/read`).catch(() => {});
                    navigate(`/course/${s.course_id}`);
                  }}>
                  <div className="flex items-center gap-3">
                    {!s.read && <div className="w-2 h-2 rounded-full bg-blue-500 shrink-0" />}
                    <div>
                      <p className="text-sm font-medium">{s.course_title}</p>
                      <p className="text-xs text-muted-foreground">Shared by {s.sender_name} {s.message && `· "${s.message}"`}</p>
                    </div>
                  </div>
                  <Button size="sm" variant="ghost">View</Button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Feedback Dialog */}
      <Dialog open={!!feedbackDialog} onOpenChange={() => { setFeedbackDialog(null); setComment(""); setRating(5); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Star className="w-5 h-5 text-amber-500" /> Rate this Course</DialogTitle>
            <DialogDescription>{feedbackDialog?.title}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Rating</Label>
              <div className="flex gap-1 mt-1">
                {[1, 2, 3, 4, 5].map(r => (
                  <button key={r} onClick={() => setRating(r)} className="p-0.5" data-testid={`star-${r}`}>
                    <Star className={`w-6 h-6 ${r <= rating ? "fill-amber-400 text-amber-400" : "text-muted"}`} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Comment (optional)</Label>
              <Textarea value={comment} onChange={e => setComment(e.target.value)} placeholder="Share your thoughts..." className="mt-1" data-testid="feedback-comment" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFeedbackDialog(null)}>Cancel</Button>
            <Button onClick={submitFeedback} data-testid="submit-feedback-btn">Submit Feedback</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report Dialog */}
      <Dialog open={!!reportDialog} onOpenChange={() => { setReportDialog(null); setComment(""); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Flag className="w-5 h-5 text-red-500" /> Report Content</DialogTitle>
            <DialogDescription>Report an issue with "{reportDialog?.title}" to TTS</DialogDescription>
          </DialogHeader>
          <div>
            <Label>Describe the issue</Label>
            <Textarea value={comment} onChange={e => setComment(e.target.value)} placeholder="What's wrong with this content?" className="mt-1" data-testid="report-description" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportDialog(null)}>Cancel</Button>
            <Button variant="destructive" onClick={submitReport} disabled={!comment.trim()} data-testid="submit-report-btn">Submit Report</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suggest Edits Dialog */}
      <Dialog open={!!suggestDialog} onOpenChange={() => { setSuggestDialog(null); setComment(""); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Edit3 className="w-5 h-5 text-blue-500" /> Suggest Edits</DialogTitle>
            <DialogDescription>Suggest improvements for "{suggestDialog?.title}"</DialogDescription>
          </DialogHeader>
          <div>
            <Label>Your suggestion</Label>
            <Textarea value={comment} onChange={e => setComment(e.target.value)} placeholder="What would you improve?" className="mt-1" data-testid="suggest-text" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSuggestDialog(null)}>Cancel</Button>
            <Button onClick={submitSuggestion} disabled={!comment.trim()} data-testid="submit-suggest-btn">Submit Suggestion</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <ShareDialog
        open={!!shareDialog}
        onOpenChange={(v) => { if (!v) setShareDialog(null); }}
        itemType="course"
        itemId={shareDialog?.course_id || ""}
        itemTitle={shareDialog?.title || ""}
      />
    </div>
  );

  // Wrap in appropriate layout based on role
  if (user?.role === "tenant_admin") {
    return <AdminLayout title="My Courses" variant="tenant_admin">{pageContent}</AdminLayout>;
  }
  if (user?.role === "super_admin") {
    return <AdminLayout title="My Courses" variant="super_admin">{pageContent}</AdminLayout>;
  }
  if (user?.role === "manager") {
    return <AdminLayout title="My Courses" variant="manager">{pageContent}</AdminLayout>;
  }
  // Learner / Trainer
  return <AdminLayout title="My Courses" variant="learner">{pageContent}</AdminLayout>;
};

// Course Row Component
const CourseRow = ({ course: c, navigate, onFavorite, onFeedback, onReport, onSuggest, onShare, onCopyLink }) => {
  const [showActions, setShowActions] = useState(false);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      className="flex items-center gap-4 p-3 rounded-xl border hover:border-primary/20 transition-all group" data-testid={`course-row-${c.course_id}`}>
      {/* Thumbnail */}
      <div className="w-20 h-14 rounded-lg overflow-hidden shrink-0 cursor-pointer" onClick={() => navigate(`/course/${c.course_id}`)}>
        {c.thumbnail_url ? (
          <img src={c.thumbnail_url} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center">
            <BookOpen className="w-5 h-5 text-white/30" />
          </div>
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/course/${c.course_id}`)}>
        <div className="flex items-center gap-2">
          <h4 className="font-medium text-sm truncate">{c.title}</h4>
          <Badge variant="outline" className="text-[9px] shrink-0">{typeLabels[c.course_type] || c.course_type}</Badge>
          {c.assignment_type === "mandatory" && (
            <Badge data-testid="badge-mandatory" className="text-[9px] bg-red-500/10 text-red-600 border-red-500/20 shrink-0">Mandatory</Badge>
          )}
          {c.assignment_type === "recommended" && (
            <Badge data-testid="badge-recommended" className="text-[9px] bg-blue-500/10 text-blue-600 border-blue-500/20 shrink-0">Recommended</Badge>
          )}
          {c.assignment_type === "self_enrolled" && (
            <Badge data-testid="badge-self-enrolled" className="text-[9px] bg-slate-500/10 text-slate-500 border-slate-500/20 shrink-0">Self-enrolled</Badge>
          )}
          {c.is_compliance && (
            <Badge data-testid="badge-compliance" className="text-[9px] bg-orange-500/10 text-orange-600 border-orange-500/20 shrink-0">Compliance</Badge>
          )}
        </div>
        <p className="text-[10px] text-muted-foreground mt-0.5">{c.category} · {c.duration_hours}h</p>
      </div>

      {/* Progress */}
      <div className="w-28 shrink-0">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] text-muted-foreground">{c.progress}%</span>
          <Badge className={`text-[9px] px-1.5 py-0 ${statusColors[c.status] || statusColors.enrolled}`}>
            {c.status === "completed" ? "Done" : c.status === "in_progress" ? "In Progress" : "New"}
          </Badge>
        </div>
        <Progress value={c.progress} className="h-1.5" />
        {c.time_spent_seconds > 0 && (
          <p className="text-[9px] text-muted-foreground mt-1 flex items-center gap-0.5">
            <Clock className="w-2.5 h-2.5" />
            {c.time_spent_seconds >= 3600 ? `${Math.floor(c.time_spent_seconds / 3600)}h ${Math.floor((c.time_spent_seconds % 3600) / 60)}m` : `${Math.floor(c.time_spent_seconds / 60)}m`} spent
          </p>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 shrink-0">
        <button onClick={(e) => { e.stopPropagation(); onFavorite(c.course_id); }}
          className="p-1.5 rounded-md hover:bg-muted transition-colors" data-testid={`fav-${c.course_id}`}>
          <Heart className={`w-4 h-4 ${c.is_favorite ? "fill-red-500 text-red-500" : "text-muted-foreground"}`} />
        </button>
        <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-primary" onClick={() => navigate(`/course/${c.course_id}`)} data-testid={`launch-${c.course_id}`}>
          <Play className="w-3.5 h-3.5" /> {c.status === "completed" ? "Review" : c.status === "in_progress" ? "Resume" : "Start"}
        </Button>

        {/* More actions dropdown */}
        <div className="relative">
          <button onClick={(e) => { e.stopPropagation(); setShowActions(!showActions); }}
            className="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground">
            <MessageSquare className="w-3.5 h-3.5" />
          </button>
          {showActions && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setShowActions(false)} />
              <div className="absolute right-0 top-full mt-1 z-40 bg-popover border rounded-lg shadow-lg py-1 w-40" data-testid={`actions-menu-${c.course_id}`}>
                {[
                  { label: "Share", icon: Share2, action: () => onShare(c) },
                  { label: "Copy Link", icon: Copy, action: () => { onCopyLink(c.course_id); setShowActions(false); } },
                  { label: "Give Feedback", icon: Star, action: () => onFeedback(c) },
                  { label: "Suggest Edits", icon: Edit3, action: () => onSuggest(c) },
                  { label: "Report Issue", icon: Flag, action: () => onReport(c), danger: true },
                ].map(a => (
                  <button key={a.label} onClick={(e) => { e.stopPropagation(); a.action(); setShowActions(false); }}
                    className={`flex items-center gap-2 w-full px-3 py-1.5 text-xs hover:bg-muted transition-colors ${a.danger ? "text-red-500" : ""}`}>
                    <a.icon className="w-3.5 h-3.5" /> {a.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default MyLearningPage;
